<script setup lang="ts">
import RatingEmoji from './RatingEmoji.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import GoogleIcon from '@/assets/icons/GoogleIcon.vue';
import FormTextarea from '@/core/forms/FormTextarea.vue';
import { openUrlInNewTab } from '@/utils/dom.utils';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { USER_FEEDBACK_MESSAGE_MAX_LENGTH } from 'common/constants/user.feedback.constants';

const props = defineProps<{
  selectedStar: number;
}>();

const { t } = useI18n();

const showFeedback = ref<boolean>(false);
const feedbackText = ref<string | null>(null);

const emit = defineEmits<{
  (event: 'submit-text-feedback', value: string): void;
  (event: 'open-google-rating'): void;
  (event: 'open-direct-feedback'): void;
}>();

const handleClickShowFeedback = (): void => {
  emit('open-direct-feedback');
  showFeedback.value = true;
};

const redirectToGoogleRating = (): void => {
  emit('open-google-rating');
  const googleRatingUrl = 'https://g.page/r/CcV-mP-oINeIEBM/review';
  openUrlInNewTab(googleRatingUrl);
};

const showFeedbackTextarea = computed((): boolean => {
  return props.selectedStar <= 3 || showFeedback.value;
});

const handleSubmitTextFeedback = (): void => {
  emit('submit-text-feedback', feedbackText.value ?? '');
};

const getDescription = computed((): string => {
  if (props.selectedStar === 0) {
    return t('help.rating.description');
  }
  if (props.selectedStar === 3 || props.selectedStar === 2) {
    return t('help.rating.improveRatingDescription2And3Stars');
  }
  if (props.selectedStar === 1) {
    return t('help.rating.improveRatingDescription1Star');
  }
  return t('help.rating.greatRatingDescription');
});

const getTitle = computed((): string => {
  if (props.selectedStar === 0) {
    return t('help.rating.title');
  }
  if (props.selectedStar <= 3) {
    return t('help.rating.improveRatingTitle');
  }
  return t('help.rating.greatRatingTitle');
});
</script>

<template>
  <div class="flex gap-y-xl flex-col text-center">
    <RatingEmoji :level="props.selectedStar" class="w-[80px] h-[80px] mx-auto" />
    <h1 class="text-title leading-[36px] font-medium">
      {{ getTitle }}
    </h1>
    <p class="text-body leading-[27px] font-normal">
      {{ getDescription }}
    </p>
    <div
      v-if="!showFeedbackTextarea && (props.selectedStar == 4 || props.selectedStar == 5)"
      class="flex flex-col gap-xl leading-[27px]"
    >
      <CustomButton
        type="button"
        size="lg"
        theme="primary"
        class="mx-auto w-full flex items-center gap-sm justify-center"
        @click="redirectToGoogleRating"
      >
        <GoogleIcon class="size-icon-md" />
        {{ $t('help.rating.rateOnGoogle') }}
      </CustomButton>
      <CustomButton
        type="button"
        size="lg"
        theme="link"
        class="mx-auto w-full flex items-center gap-sm justify-center"
        @click="handleClickShowFeedback"
      >
        {{ $t('help.rating.directFeedback') }}
      </CustomButton>
    </div>
    <div v-if="showFeedbackTextarea" class="flex flex-col gap-xl">
      <FormTextarea
        name="feedback"
        :maxlength="USER_FEEDBACK_MESSAGE_MAX_LENGTH"
        :value="feedbackText ?? undefined"
        :autofocus="true"
        :rows="4"
        :placeholder="$t('help.rating.textPlaceholder')"
        @update:value="(value) => (feedbackText = value)"
      />
      <CustomButton
        type="button"
        size="lg"
        theme="primary"
        class="mx-auto w-full flex items-center gap-sm justify-center"
        @click="handleSubmitTextFeedback"
      >
        {{ $t('help.rating.sendFeedback') }}
      </CustomButton>
    </div>
  </div>
</template>
